@file:OptIn(ExperimentalBlockRichTextApi::class)

package com.taocent.simple.compose.component.blockrichtext

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue
import com.taocent.simple.compose.component.blockrichtext.internal.block.EditorCursor

/**
 * BlockState 表格行列增删改、单元格内容、整体删除相关的扩展函数。
 *
 * - [updateTable] 整体替换表格块
 * - [insertTableRow] / [insertTableColumn] 插入行/列
 * - [deleteTableRow] / [deleteTableColumn] 删除行/列
 * - [moveTableRow] / [moveTableColumn] 拖拽重排行/列
 * - [insertTableAbove] 在指定 table 上方插入新表格
 * - [updateTableCellContent] 同步单元格内容(自带 undo 抑制)
 * - [deleteTable] 整体删除表格(支持上下文本块合并)
 * - [deleteSelectedTable] / [clearTableSelection] 选中态操作
 */

fun BlockState.updateTable(tableId: String, newTable: TableBlock) {
    val index = findBlockIndex(tableId)
    if (index >= 0) {
        val before = documentSnapshot()
        _blocks[index] = newTable
        recordDocumentChange(before)
    }
}

fun BlockState.insertTableRow(tableId: String, afterRowIndex: Int, insertAbove: Boolean) {
    val index = findBlockIndex(tableId)
    if (index < 0) return
    val block = _blocks[index]
    if (block !is TableBlock) return
    val before = documentSnapshot()
    val insertIndex = if (insertAbove) afterRowIndex else afterRowIndex + 1
    val newCellCount = block.columnCount
    val newRow = TableBlock.TableRow(
        cells = List(newCellCount) { TableBlock.TableCell() },
        isHeader = false
    )
    val newRows = block.rows.toMutableList().apply { add(insertIndex, newRow) }
    _blocks[index] = block.copy(rows = newRows)
    recordDocumentChange(before)
}

fun BlockState.insertTableColumn(tableId: String, afterColumnIndex: Int, insertLeft: Boolean) {
    val index = findBlockIndex(tableId)
    if (index < 0) return
    val block = _blocks[index]
    if (block !is TableBlock) return
    val before = documentSnapshot()
    val insertIndex = if (insertLeft) afterColumnIndex else afterColumnIndex + 1
    val newRows = block.rows.map { row ->
        val newCells = row.cells.toMutableList().apply {
            add(insertIndex, TableBlock.TableCell())
        }
        row.copy(cells = newCells)
    }
    _blocks[index] = block.copy(rows = newRows, columnCount = block.columnCount + 1)
    recordDocumentChange(before)
}

fun BlockState.deleteTableRow(tableId: String, rowIndex: Int) {
    val index = findBlockIndex(tableId)
    if (index < 0) return
    val block = _blocks[index]
    if (block !is TableBlock) return
    if (block.rows.size <= 1) return
    val before = documentSnapshot()
    val newRows = block.rows.toMutableList().apply { removeAt(rowIndex) }
    _blocks[index] = block.copy(rows = newRows)
    recordDocumentChange(before)
}

fun BlockState.deleteTableColumn(tableId: String, columnIndex: Int) {
    val index = findBlockIndex(tableId)
    if (index < 0) return
    val block = _blocks[index]
    if (block !is TableBlock) return
    if (block.columnCount <= 1) return
    val before = documentSnapshot()
    val newRows = block.rows.map { row ->
        val newCells = row.cells.toMutableList().apply { removeAt(columnIndex) }
        row.copy(cells = newCells)
    }
    _blocks[index] = block.copy(rows = newRows, columnCount = block.columnCount - 1)
    recordDocumentChange(before)
}

/**
 * 直接根据 tableId 删除整个表格块。
 *
 * 与 `deleteTableRow` / `deleteTableColumn` 命名风格一致,接受 tableId 即可,
 * 不依赖 `selectedTableBlockId` 选中态 — 这是为了支持从行/列菜单直接调用的场景
 * (菜单里点"删除当前表格"时,UI 选中态可能还没同步到 BlockState.selectedTableBlockId)。
 *
 * 复用 `deleteSelectedTable` 的删除 + 上下文本块合并 + 焦点处理逻辑,
 * 把选中态判断拆出来:`deleteSelectedTable` 委托给本方法。
 */
fun BlockState.deleteTable(tableId: String): Boolean {
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val block = _blocks[index]
    if (block !is TableBlock) return false

    val before = documentSnapshot()
    val previousBlock = if (index > 0) _blocks[index - 1] else null
    val nextBlock = if (index + 1 < _blocks.size) _blocks[index + 1] else null

    _blocks.removeAt(index)
    blockStates.remove(tableId)
    if ((editorCursor as? EditorCursor.TableCell)?.tableId == tableId) {
        clearTableCellFocus()
    }
    if (selectedTableBlockId == tableId) {
        selectedTableBlockId = null
    }

    if (previousBlock is TextBlock && nextBlock is TextBlock) {
        val prevIndex = findBlockIndex(previousBlock.id)
        if (prevIndex >= 0) {
            val prevState = getBlockState(previousBlock.id)
            val nextState = getBlockState(nextBlock.id)
            val prevContent = prevState.textFieldValue.annotatedString
            val nextContent = nextState.textFieldValue.annotatedString
            val mergedBuilder = AnnotatedString.Builder(prevContent.length + nextContent.length)
            mergedBuilder.append(prevContent)
            mergedBuilder.append(nextContent)
            val merged = mergedBuilder.toAnnotatedString()
            prevState.restoreTextFieldValue(
                TextFieldValue(
                    annotatedString = merged,
                    selection = TextRange(prevContent.length)
                )
            )
            syncBlockContent(previousBlock.id)
            val nextIndex = findBlockIndex(nextBlock.id)
            if (nextIndex >= 0) {
                _blocks.removeAt(nextIndex)
                blockStates.remove(nextBlock.id)
            }
            setFocusedBlockCompat(previousBlock.id, requestFocus = true)
        }
    } else if (nextBlock != null) {
        setFocusedBlockCompat(nextBlock.id, requestFocus = true)
    } else if (previousBlock != null) {
        setFocusedBlockCompat(previousBlock.id, requestFocus = true)
    }

    if (_blocks.isEmpty()) {
        val newBlock = TextBlock()
        _blocks.add(newBlock)
        blockStates[newBlock.id] = createBlockState()
        setFocusedBlockCompat(newBlock.id)
    }

    recordDocumentChange(before)
    return true
}

fun BlockState.updateTableCellContent(
    tableId: String,
    rowId: String,
    cellId: String,
    content: AnnotatedString
) {
    val index = findBlockIndex(tableId)
    if (index < 0) return
    val block = _blocks[index]
    if (block !is TableBlock) return
    val oldContent = block.rows
        .firstOrNull { it.rowId == rowId }
        ?.cells
        ?.firstOrNull { it.cellId == cellId }
        ?.content
    if (oldContent == content) return
    val before = if (!suppressContentUndoRecord) documentSnapshot() else null
    val newRows = block.rows.map { row ->
        if (row.rowId != rowId) {
            row
        } else {
            row.copy(
                cells = row.cells.map { cell ->
                    if (cell.cellId == cellId) cell.copy(content = content) else cell
                }
            )
        }
    }
    _blocks[index] = block.copy(rows = newRows)
    if (before != null) {
        recordDocumentChange(before, mergeKey = "cell:$tableId:$rowId:$cellId")
    }
}

fun BlockState.moveTableRow(tableId: String, fromIndex: Int, toIndex: Int) {
    if (fromIndex == toIndex) return
    val index = findBlockIndex(tableId)
    if (index < 0) return
    val block = _blocks[index]
    if (block !is TableBlock) return
    if (fromIndex < 0 || fromIndex >= block.rows.size) return
    if (toIndex < 0 || toIndex >= block.rows.size) return
    val before = documentSnapshot()
    val newRows = block.rows.toMutableList()
    val row = newRows.removeAt(fromIndex)
    newRows.add(toIndex, row)
    _blocks[index] = block.copy(rows = newRows)
    recordDocumentChange(before)
}

fun BlockState.moveTableColumn(tableId: String, fromIndex: Int, toIndex: Int) {
    if (fromIndex == toIndex) return
    val index = findBlockIndex(tableId)
    if (index < 0) return
    val block = _blocks[index]
    if (block !is TableBlock) return
    if (fromIndex < 0 || fromIndex >= block.columnCount) return
    if (toIndex < 0 || toIndex >= block.columnCount) return
    val before = documentSnapshot()
    val newRows = block.rows.map { row ->
        val newCells = row.cells.toMutableList()
        val cell = newCells.removeAt(fromIndex)
        newCells.add(toIndex, cell)
        row.copy(cells = newCells)
    }
    _blocks[index] = block.copy(rows = newRows)
    recordDocumentChange(before)
}

fun BlockState.insertTableAbove(tableId: String, rows: Int, columns: Int): Boolean {
    val index = findBlockIndex(tableId)
    if (index < 0) return false
    val sourceBlockId = findNearestTextBlockId(index)
    val before = documentSnapshot()
    val tableBlock = TableBlock.create(rows, columns)
    _blocks.add(index, tableBlock)
    recordDocumentChange(before)
    if (sourceBlockId != null) {
        navSelectTable(tableId, sourceBlockId)
    } else {
        setBlockAnchorCursor(tableId)
    }
    return true
}

fun BlockState.deleteSelectedTable(): Boolean {
    val tableId = selectedTableBlockId ?: return false
    return deleteTable(tableId)
}

fun BlockState.clearTableSelection() {
    selectedTableBlockId = null
}
